import { useQuery } from "@tanstack/react-query"
import { Address, erc4626Abi } from "viem"
import { useReadContract } from "wagmi"

import {
  GOLDSKY_MEZO_ERC_20_SUBGRAPH_URL,
  MEZO_CHAIN,
  query,
  queryKeysFactory,
} from "#/shared/constants"

const limitForOneQuery = 1_000

const buildQuery = (lastId: string, vault: Address) => `
      query {
        balances (
          where: {
            id_gt: "${lastId}",
            balance_gt: "0",
            token_contains_nocase: "${vault}"
          }
          orderBy: id
          orderDirection: asc
          first: ${limitForOneQuery}
        ) {
          id
        }
      }`

const fetchTotalDepositors = async (
  vault: Address,
  lastId = "",
  totalDepositors = 0,
): Promise<number> => {
  const subgraphQuery = buildQuery(lastId, vault)

  const response = await fetch(GOLDSKY_MEZO_ERC_20_SUBGRAPH_URL, {
    method: "POST",
    body: JSON.stringify({ query: subgraphQuery }),
    headers: { "Content-Type": "application/json" },
  })

  if (!response.ok)
    throw new Error(`Failed to fetch total depositors in vault (${vault})`)

  const result = (await response.json()) as {
    data: { balances: { id: string }[] }
  }

  if (result.data.balances.length === 0) {
    return totalDepositors
  }

  const numberOfDepositorsFromQuery = result.data.balances.length

  const updatedLastId = result.data.balances[numberOfDepositorsFromQuery - 1].id

  const updatedTotalDepositors = totalDepositors + numberOfDepositorsFromQuery

  return fetchTotalDepositors(vault, updatedLastId, updatedTotalDepositors)
}

export default function useVaultStats(vault: Address) {
  const {
    data,
    isFetching: isFetchingTotalAssets,
    ...rest
  } = useReadContract({
    abi: erc4626Abi,
    address: vault,
    functionName: "totalAssets",
    chainId: MEZO_CHAIN.id,
    query: {
      refetchInterval: query.DEFAULT_REFETCH_INTERVAL,
      staleTime: query.DEFAULT_STALE_TIME,
    },
  })

  const { data: totalDepositors, isFetching: isFetchingTotalDepositors } =
    useQuery({
      queryKey: [queryKeysFactory.mezoKeys.mezoVaultsStats(), vault],
      queryFn: () => fetchTotalDepositors(vault),
      refetchInterval: query.DEFAULT_REFETCH_INTERVAL,
      staleTime: query.DEFAULT_STALE_TIME,
    })

  return {
    stats: { totalDeposited: data, totalDepositors },
    ...rest,
    isFetching: isFetchingTotalDepositors || isFetchingTotalAssets,
  }
}
